import moment from "moment";

import { get, cloneDeep } from "lodash";

import {
    NullableNumber,
    ReportOverallData,
    ReportIndividualData,
    ReportChartSeries,
    ReportSummaryInfoOfOneItem,
    ReportTimeRangeType,
    ReportItemsState,
    ReportTabType,
    ReportItemsLoading,
    ReportRawDeviceData,
    ReportRawDataCalculationType,
    ReportRoom,
    ReportRoomUsageData,
} from "types/Reports";
import { ConsumptionChartBy } from "types/Charts";
import { REPORT_ITEM_LIST } from "constant/report";
import {
    calculatePercentage,
} from "utils/functions";
import { getWeekdaysWeekendsIndexInMonth } from "utils/datetime";
import { OrganisationDto } from "types/Organisation";
import { getOrgOwnerName } from "utils/organisationFunction";
import { SensorDetails } from "types/Sensors";
import { groupDataByTime, MetricData } from "service/proqueryService";
import { Device } from "types";
import {
    getMinMaxFromConsumptionArray,
    sumByWithNull,
    sumArrayAndIgnoreNaN,
    getAverageAndPercentFromSeries,
    getTotalAndPercentFromSeries,
} from "./reportArray";
import { formatDataInReportChartSeries } from "./reportFormat";
import {
    avgValueAndGroupRawDataByDay,
    computeIndexByDayInMonth,
    diffValueAndGroupRawDataByDay,
    sumPulseAndGroupRawDataByDay,
} from "./reportCompute";
import { convertRoomMetricValuesToData } from "./reportConvert";

export const getSeriesDataLength = (series: ReportChartSeries[]) => {
    let length = 0;

    for (const sensorData of series) {
        if (sensorData.data.length > 0) {
            length = sensorData.data.length;
            break;
        }
    }

    return length;
};

export const getStatusOrOccupiedSensorFromLDSU = (
    roomSensors: Device[],
    deviceLDSUID: string,
    deviceSAID: number,
) => {
    let statusDevice: Device | undefined;
    let occupancyDevice: Device | undefined;

    for (const sensor of roomSensors) {
        if (sensor.ldsuUID === deviceLDSUID && sensor.said === deviceSAID) {
            if (sensor.said === 0) {
                // room status
                statusDevice = cloneDeep(sensor);
            } else if (sensor.said === 1) {
                // room occupancy
                occupancyDevice = cloneDeep(sensor);
            }
            break;
        }
    }

    return { statusDevice, occupancyDevice };
}

export const makeNullForFutureInItemSeries = (
    input: NullableNumber[],
    categories: string[],
    endDayOfMonth: moment.Moment,
    curDay: moment.Moment,
) => {
    const data: NullableNumber[] = [...input];

    if (endDayOfMonth.isSame(curDay, 'month')) {
        const date = curDay.date();
        const idx = computeIndexByDayInMonth(categories, date);

        if (idx !== null) {
            for (let i = idx + 1; i < data.length; i++) {
                data[i] = null;
            }
        }
    }

    return data;
};

const CALCULATION_STRATEGIES: Record<
    ReportRawDataCalculationType,
    (
        values: [number, string][],
        categories: string[],
        endDayOfMonth: moment.Moment,
    ) => NullableNumber[]
> = {
    diff: (values, categories, endDayOfMonth) =>
        diffValueAndGroupRawDataByDay(values, categories, endDayOfMonth),
    avg: (values, categories) =>
        avgValueAndGroupRawDataByDay(values, categories),
    sumPulse: (values, categories, endDayOfMonth) =>
        sumPulseAndGroupRawDataByDay(values, categories, endDayOfMonth),
};

const isValidRawDeviceData = (rawDeviceData: ReportRawDeviceData[]): boolean =>
    rawDeviceData?.length >= 1 && rawDeviceData[0].values?.length >= 1;

/**
 *
 * @param id ID of the sensor
 * @param name Name of the sensor
 * @param rawDeviceData The array of intervals in a month
 * @param categories The category day of a month
 * @param endDayOfMonth The last day of a month
 * @param calculationType The calculation type based on sensor type
 * @returns Chart series
 */
export const createItemSeriesFromRawData = (
    id: string,
    name: string,
    rawDeviceData: any,
    categories: string[],
    endDayOfMonth: moment.Moment,
    calculationType: 'diff' | 'avg' | 'sumPulse',
) => {
    const itemSeries: ReportChartSeries = {
        id,
        name,
        data: [],
        weekdaysData: [],
        weekendsData: [],
    };

    if (!isValidRawDeviceData(rawDeviceData)) {
        return itemSeries;
    }

    const allDays = CALCULATION_STRATEGIES[calculationType](
        rawDeviceData[0].values,
        categories,
        endDayOfMonth,
    );

    // Make null of all future days
    const data = makeNullForFutureInItemSeries(allDays, categories, endDayOfMonth, moment());

    itemSeries.data = cloneDeep(data);
    itemSeries.weekdaysData = cloneDeep(data);
    itemSeries.weekendsData = cloneDeep(data);

    // get weekdays indexes and weekends indexes in the array allDays
    const { weekdayIndexes, weekendIndexes } = getWeekdaysWeekendsIndexInMonth(endDayOfMonth);

    for (let i = 0; i < itemSeries.data.length; i++) {
        if (weekdayIndexes.includes(i)) {
            itemSeries.weekendsData[i] = null;
        } else if (weekendIndexes.includes(i)) {
            itemSeries.weekdaysData[i] = null;
        }
    }

    return itemSeries;
};

export const createItemSeriesFromRoomUsageData = (
    roomUsageData: ReportRoomUsageData,
    rooms: ReportRoom[],
    categories: string[],
    endDayOfMonth: moment.Moment,
) => {
    const series: ReportChartSeries[] = [];
    let isInvalid = true;

    for (const room of rooms) {
        const itemSeries: ReportChartSeries = {
            id: room.roomUUId,
            name: room.roomName,
            data: [],
            weekdaysData: [],
            weekendsData: [],
        };

        if (!room.statusDevice) {
            series.push(itemSeries);

            continue;
        }

        const metricData = roomUsageData.metricData.find(
            (item) =>
                room.statusDevice &&
                item.device_id === room.statusDevice.ldsuUID &&
                item.gateway_id === room.statusDevice.gatewayId &&
                item.said === room.statusDevice.said,
        );

        if (!metricData) {
            series.push(itemSeries);

            continue;
        }

        if (metricData.values.length > 0) {
            isInvalid = false;
        }

        const allDays = convertRoomMetricValuesToData(
            metricData.values,
            categories,
        );
        // Make null of all future days
        const data = makeNullForFutureInItemSeries(allDays, categories, endDayOfMonth, moment());

        itemSeries.data = cloneDeep(data);
        itemSeries.weekdaysData = cloneDeep(data);
        itemSeries.weekendsData = cloneDeep(data);

        // get weekdays indexes and weekends indexes in the array allDays
        const { weekdayIndexes, weekendIndexes } = getWeekdaysWeekendsIndexInMonth(endDayOfMonth);

        for (let i = 0; i < itemSeries.data.length; i++) {
            if (weekdayIndexes.includes(i)) {
                itemSeries.weekendsData[i] = null;
            } else if (weekendIndexes.includes(i)) {
                itemSeries.weekdaysData[i] = null;
            }
        }

        series.push(itemSeries);
    }

    return { series, isInvalid };
};

export const summaryReportChartSeries = (
    isCurrentInvalid: boolean,
    isPreviousInvalid: boolean,
    curSeries: ReportChartSeries[],
    previousSeries: ReportChartSeries[],
): ReportSummaryInfoOfOneItem => {
    let total: NullableNumber;
    let previousTotal: NullableNumber;
    let percent: NullableNumber;
    let totalWeekdays: NullableNumber;
    let previousTotalWeekdays: NullableNumber;
    let weekdaysPercent: NullableNumber;
    let totalWeekends: NullableNumber;
    let previousTotalWeekends: NullableNumber;
    let weekendsPercent: NullableNumber;

    if (isCurrentInvalid) {
        total = null;
        totalWeekdays = null;
        totalWeekends = null;
    } else {
        total = sumByWithNull(curSeries, (item) => sumArrayAndIgnoreNaN(item.data));
        totalWeekdays = sumByWithNull(curSeries, (item) => sumArrayAndIgnoreNaN(item.weekdaysData));
        totalWeekends = sumByWithNull(curSeries, (item) => sumArrayAndIgnoreNaN(item.weekendsData));
    }

    if (isPreviousInvalid) {
        previousTotal = null;
        previousTotalWeekdays = null;
        previousTotalWeekends = null;
    } else {
        previousTotal = sumByWithNull(previousSeries, (item) => sumArrayAndIgnoreNaN(item.data));
        previousTotalWeekdays = sumByWithNull(previousSeries, (item) => sumArrayAndIgnoreNaN(item.weekdaysData));
        previousTotalWeekends = sumByWithNull(previousSeries, (item) => sumArrayAndIgnoreNaN(item.weekendsData));
    }

    if (isCurrentInvalid || isPreviousInvalid) {
        percent = null;
        weekdaysPercent = null;
        weekendsPercent = null;
    } else {
        percent = calculatePercentage(total, previousTotal);
        weekdaysPercent = calculatePercentage(totalWeekdays, previousTotalWeekdays);
        weekendsPercent = calculatePercentage(totalWeekends, previousTotalWeekends);
    }

    const newSummaryInfoOfItem: ReportSummaryInfoOfOneItem = {
        all: {
            total,
            previousTotal,
            percent,
        },
        weekdays: {
            total: totalWeekdays,
            previousTotal: previousTotalWeekdays,
            percent: weekdaysPercent,
        },
        weekends: {
            total: totalWeekends,
            previousTotal: previousTotalWeekends,
            percent: weekendsPercent,
        },
    };

    return newSummaryInfoOfItem;
};

export const computeReportChartSeries = (
    sensorDetails: SensorDetails[],
    groupBy: ConsumptionChartBy,
    metricData: MetricData[],
    lastTime: moment.Moment,
) => {
    let categories: string[] | number[] = [];
    const { weekdayIndexes, weekendIndexes } = getWeekdaysWeekendsIndexInMonth(lastTime);

    const series: ReportChartSeries[] = sensorDetails.map((details) => {
        const weekdaysData: NullableNumber[] = [];
        const weekendsData: NullableNumber[] = [];
        const allDaysData: NullableNumber[] = [];
        const rawData = metricData.find(
            (data: MetricData) =>
                data.device_id === details.device_id &&
                data.said === details.said
        );

        let data = [];
        const id = `${details.gateway_id ?? ""}_${details.device_id ?? ""}_${details.said ?? 0}`;

        if (!rawData) {
            return {
                id,
                name: details.name,
                data: allDaysData,
                weekdaysData,
                weekendsData,
            };
        }

        const groupData = groupDataByTime(
            groupBy,
            get(rawData, "values", []),
            lastTime
        );
        data = groupData.data;

        if (!categories?.length) {
            categories = groupData.categories;
        }

        if (data && data.length > 0) {
            const result = formatDataInReportChartSeries(data, weekdayIndexes, weekendIndexes);

            return {
                id,
                name: details.name,
                data: result.allDaysData,
                weekdaysData: result.weekdaysData,
                weekendsData: result.weekendsData,
            };
        }

        return {
            id,
            name: details.name,
            data: allDaysData,
            weekdaysData,
            weekendsData,
        };
    });

    return { series, categories: categories.map((item) => `${item}`) };
};

export const getReportOrganizationName = (orgInfo: OrganisationDto) => {
    if (orgInfo.isDefault && orgInfo.ownerName) {
        return getOrgOwnerName(orgInfo);
    }

    return orgInfo.name;
};

export const isReportOverallDataValid = (data: ReportOverallData, timeRangeType: ReportTimeRangeType) => {
    if (data.categories.length === 0) {
        return false;
    }

    const arr: NullableNumber[] = data[timeRangeType].data;

    return arr.some((item => item !== null));
};

export const isReportIndividualDatasValid = (data: ReportIndividualData[], timeRangeType: ReportTimeRangeType) => {
    if (data.length === 0) {
        return false;
    }

    let isValid = false;

    for (const item of data) {
        let value: NullableNumber;

        value = item[timeRangeType].value;

        if (value !== null) {
            isValid = true;
            break;
        }
    }

    return isValid;
};

export const isDisableReportBtnAction = (
    items: Readonly<ReportItemsState>,
    itemsLoading: Readonly<ReportItemsLoading>,
) => {
    let isDisableByItem = true;
    let isDisableBySelectedItem = true;
    let isDisableByLoading = false;

    for (const itemType of REPORT_ITEM_LIST) {
        const type = itemType;

        if (items[type].appliedIds.length > 0) {
            isDisableByItem = false;
        }

        if (items[type].selectedIds.length > 0) {
            isDisableBySelectedItem = false;
        }
    }

    for (const itemType of REPORT_ITEM_LIST) {
        const type = itemType;

        if (itemsLoading[type] === true) {
            isDisableByLoading = true;
            break;
        }
    }

    return {
        disableExport: isDisableByItem || isDisableByLoading,
        disableApply: isDisableBySelectedItem || isDisableByLoading,
    };
};

export const isReportChartSeriesValid = (items: ReportChartSeries[]) => {
    for (const item of items) {
        if (item.data.length > 0) {
            return true;
        }
    }

    return false;
};

export const handlePDFValueOverflow = (valueStr: string, maxLength: number, unitLength: number) => {
    const allowedNumberLength = maxLength - unitLength;

    if (valueStr.length < allowedNumberLength) {
        return valueStr;
    }

    const firstPart = valueStr.slice(0, allowedNumberLength);
    const secondPart = valueStr.slice(allowedNumberLength);

    return firstPart + '\n' + secondPart;
};

export const getDataOfReportChartSeries = (
    series: ReportChartSeries,
    timeRangeType: ReportTimeRangeType,
) => {
    let data: NullableNumber[] = [];

    switch (timeRangeType) {
        case 'weekends':
            data = [...series.weekendsData];
            break;
        case 'weekdays':
            data = [...series.weekdaysData];
            break;
        case 'all':
        default:
            data = [...series.data];
            break;
    }

    return data;
}

const getTabItemData = (
    curSeries: ReportChartSeries,
    prevSeries: ReportChartSeries | undefined,
    timeRangeType: ReportTimeRangeType,
) => {
    const data = getDataOfReportChartSeries(curSeries, timeRangeType);
    const prevData = prevSeries ? getDataOfReportChartSeries(prevSeries, timeRangeType) : [];
    return { data, prevData };
};

export const getAverageAndPercentForTabItem = (
    curSeries: ReportChartSeries,
    prevSeries: ReportChartSeries | undefined,
    timeRangeType: ReportTimeRangeType,
) => {
    const { data, prevData } = getTabItemData(curSeries, prevSeries, timeRangeType);
    const { avg, percent, previousAvg } = getAverageAndPercentFromSeries(data, prevData);
    return { data, avg, percent, prevData, previousAvg };
};

export const getTotalAndPercentForTabItem = (
    curSeries: ReportChartSeries,
    prevSeries: ReportChartSeries | undefined,
    timeRangeType: ReportTimeRangeType,
) => {
    const { data, prevData } = getTabItemData(curSeries, prevSeries, timeRangeType);
    const { total, percent, previousTotal } = getTotalAndPercentFromSeries(data, prevData);
    return { data, total, percent, prevData, previousTotal };
};

export const getProcessedSeriesByTimeRange = (series: ReportChartSeries[]) => {
    const length = getSeriesDataLength(series);

    return {
        all: getMinMaxFromConsumptionArray(length, series.map((item) => item.data)),
        weekdays: getMinMaxFromConsumptionArray(length, series.map((item) => item.weekdaysData)),
        weekends: getMinMaxFromConsumptionArray(length, series.map((item) => item.weekendsData)),
    };
};

export const computeNullableAverage = (count: number, total: NullableNumber): NullableNumber =>
    count !== 0 && total !== null ? total / count : null;

export const getAverageFromSeriesAndTotal = (
    total: NullableNumber,
    totalWeekdays: NullableNumber,
    totalWeekends: NullableNumber,
    series: ReportChartSeries[],
) => {
    const processed = getProcessedSeriesByTimeRange(series);

    return {
        averageAll: computeNullableAverage(processed.all.count, total),
        averageWeekdays: computeNullableAverage(processed.weekdays.count, totalWeekdays),
        averageWeekends: computeNullableAverage(processed.weekends.count, totalWeekends),
    };
};

export const getShowTabs = (items: ReportItemsState, isApplied: boolean) => {
    const usedIds = isApplied ? 'appliedIds' : 'selectedIds';

    const showTabs: ReportTabType[] = ['overview'];

    if (items.room[usedIds].length > 0) {
        showTabs.push('room');
    }

    if (
        items.energy[usedIds].length > 0 ||
        items.water[usedIds].length > 0 ||
        items.waterLeakage[usedIds].length > 0
    ) {
        showTabs.push('utilities');
    }

    if (
        items.temperature[usedIds].length > 0 ||
        items.humidity[usedIds].length > 0 ||
        items.co2[usedIds].length > 0 ||
        items.pressure[usedIds].length > 0
    ) {
        showTabs.push('environment');
    }

    if (
        items.tvoc[usedIds].length > 0 ||
        items.pm1[usedIds].length > 0 ||
        items["pm2.5"][usedIds].length > 0 ||
        items.pm10[usedIds].length > 0
    ) {
        showTabs.push('air_quality');
    }

    // check if no additional tab except the initial overview is shown, then make the tab clear to not display the tab section
    if (showTabs.length === 1) {
        showTabs.pop();
    }

    return showTabs;
};
